import { Button, message, Modal, Space, Table, TableColumnsType, TableProps } from 'antd';
import { chunksRender, getChunks } from 'views/Marketing/components/PolicyTable';
import { useResultTypeLabel } from 'views/Todo/hooks/useLabel';
import { deletePolicyResult } from './api';
import { officialResultTypes } from './TodoType';
import { get, map } from 'lodash-es';
import StatusSwitch from 'components/StatusSwitch';
import { updatePolicy, updatePolicyOrder } from 'views/Todo/service';
import { DraggableContainer, DragHandle, SortableItem } from 'components/DragTableComponent';
import { SortableContainerProps } from 'react-sortable-hoc';
import { useCallback, useEffect, useState } from 'react';
import { arrayMoveImmutable } from 'array-move';
import { useMutation } from '@tanstack/react-query';

type RT = Todo.PolicyTemplate;

export interface PolicyTableProps extends TableProps<RT> {
  onEdit: (record: RT) => void;
  onUpdate: () => Promise<any>;
  policy: Todo.Policy;
  source: 0 | 1;
}

function PolicyTable(props: PolicyTableProps) {
  const { onEdit, policy, source, onUpdate, dataSource: _dataSource, ...otherProps } = props;
  const [dataSource, setDataSource] = useState<RT[]>(_dataSource?.slice() || []);
  useEffect(() => {
    setDataSource(_dataSource?.slice() || []);
  }, [_dataSource]);
  const { mutateAsync } = useMutation({
    mutationFn: (record: string[]) => {
      return updatePolicyOrder({
        policyId: policy.id,
        resultTypeConfig: record,
      });
    },
  });

  const { t, getResultTypeLabel } = useResultTypeLabel();
  const onSortEnd = useCallback(
    ({ oldIndex, newIndex }) => {
      const sortedData = arrayMoveImmutable(dataSource.slice(), oldIndex, newIndex);
      const record = map(sortedData, 'resultType');
      mutateAsync(record).then(() => {
        setDataSource(sortedData);
      });
    },
    [dataSource, mutateAsync],
  );
  const Container = (props: SortableContainerProps) => (
    <DraggableContainer onSortEnd={onSortEnd} {...props} />
  );

  const DraggableBodyRow: React.FC<any> = ({ className, style, ...restProps }) => {
    // function findIndex base on Table rowKey props and should always be a right array index
    const index = (() => {
      let i = dataSource?.findIndex((x) => {
        return x.resultType === restProps['data-row-key'];
      });
      if (i === -1) {
        return dataSource?.findIndex((x) => {
          return !!x.children?.find((e) => e.resultType === restProps['data-row-key']);
        });
      } else {
        return i;
      }
    })();
    return <SortableItem index={index} {...restProps} />;
  };

  const columns: TableColumnsType<RT> = [
    {
      title: '',
      dataIndex: 'sort',
      width: 50,
      className: 'drag-visible',
      render: (_, record) => {
        return record.parentResultType ? null : <DragHandle />;
      },
    },
    {
      key: 'resultType',
      dataIndex: 'resultType',
      title: t('todo_solution'),
      width: 200,
      render: (value) => {
        return getResultTypeLabel(value);
      },
    },
    {
      key: 'executeContext',
      dataIndex: 'executeContext',
      title: t('aide.推荐回复'),
      render: (executeContext: RT['executeContext']) => chunksRender(getChunks(executeContext), t),
    },
    {
      key: 'enable',
      dataIndex: 'enable',
      title: t('状态'),
      width: 150,
      render: (enable: boolean, { parentResultType, resultType, children }) => {
        if (parentResultType) {
          return null;
        }
        const _enable = children?.length ? children.every(({ enable }) => enable) : enable;
        return (
          <StatusSwitch
            checked={_enable}
            onChange={(enable) =>
              updatePolicy({
                ...policy,
                templates: policy.templates.map((template) => {
                  return (children?.length ? template.parentResultType : template.resultType) !==
                    resultType
                    ? template
                    : {
                        ...template,
                        enable,
                      };
                }),
              })
                .then(() => {
                  return onUpdate();
                })
                .catch(() => {
                  message.error(t('phrase.保存失败，请稍后再试'));
                })
            }
          />
        );
      },
    },
    {
      key: 'operation',
      title: t('操作'),
      width: 150,
      render: (_, record) => {
        const disabled = officialResultTypes.includes(record.resultType);
        if (get(record, 'children.length', 0) > 0) {
          return '-';
        }

        return (
          <Space>
            <Button type="link" onClick={() => onEdit(record)} className="!px-0">
              {t('aide.编辑')}
            </Button>
            {!disabled && (
              <Button
                type="link"
                onClick={() => {
                  Modal.confirm({
                    title: t('official_delete_tip'),
                    content: t('delete_resulttype_tip'),
                    onOk: () => {
                      deletePolicyResult(policy.id, [record.resultType])
                        .then(() => {
                          onUpdate();
                          message.success(t('marketing.已删除'));
                        })
                        .catch(() => {
                          message.error(t('phrase.删除失败，请稍后再试'));
                        });
                    },
                  });
                }}
                className="!px-0"
              >
                {t('phrase.删除')}
              </Button>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <Table
      rowKey="resultType"
      scroll={{ y: 'auto' }}
      columns={columns}
      components={{
        body: {
          wrapper: Container,
          row: DraggableBodyRow,
        },
      }}
      expandable={{
        defaultExpandAllRows: true,
        indentSize: 0,
      }}
      dataSource={dataSource}
      {...otherProps}
    />
  );
}

export default PolicyTable;
